import cx from "classnames";
import { CircleSpinner } from "common/spinners";
import { Tab, TabNav } from "common/TabNav";
import TabNavSubtle from "common/TabNav/themes/TabNavSubtle.scss";
import { Seq } from "immutable";
import { deleteReport, loadReportsForIndex, loadUserReports, restoreReport } from "lib/api/rideReports";
import { commonVerbs, featureNames, fm, Msg } from "lib/localization";
import moment from "moment";
import React from "react";
import Helmet from "react-helmet";
import { FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import appStyles from "../../App/appStyles.scss";
import { rrMessages } from "../../intlMessages";
import { noReportsMessage } from "../Messages";
import styles from "./DraftList.scss";

export class DraftList extends React.Component {
  defaultTab = "#drafts";

  constructor(props) {
    super(props);
    this.state = {
      reportsLoad: "not_started",
      trashLoad: "not_started",
    };
  }

  componentDidMount() {
    this.loadData();
    if (this.props.location.hash === "#trash") {
      this.loadAllTrash();
    }
  }

  componentWillUnmount() {
    this._abortLoad = true;
  }

  componentDidUpdate() {
    this.loadData();
  }

  deleteReport(draft) {
    const { deleteReport } = this.props;
    deleteReport(draft);
  }

  loadData = () => {
    const { reportsLoad } = this.state;
    if (reportsLoad === "not_started") {
      this.loadAllReportsForUser();
    }
  };

  loadAllReportsForUser = () => {
    const { loadUserReports, currentUser } = this.props;
    this.setState({ reportsLoad: "pending" });

    const recursiveLoad = () => {
      const { topic } = this.props;
      if (topic.nextPage === -1 || this._abortLoad) {
        this.setState({ reportsLoad: "completed" });
      } else {
        loadUserReports(currentUser.id, topic.nextPage).then(recursiveLoad);
      }
    };

    loadUserReports(currentUser.id).then(recursiveLoad);
  };

  loadAllTrash = () => {
    const { loadTrash } = this.props;
    this.setState({ trashLoad: "pending" });
    // need to make sure anywhere we filter by userId we also filter by deleted_at
    const recursiveLoad = () => {
      const { trash } = this.props;
      if (trash.nextPage === -1 || this._abortLoad) {
        this.setState({ trashLoad: "completed" });
      } else {
        loadTrash(trash.nextPage).then(recursiveLoad);
      }
    };

    loadTrash().then(recursiveLoad);
  };

  onChangeTab = (selectedTab) => {
    const { history } = this.props;
    if (selectedTab === this.defaultTab) {
      history.push(window.location.pathname + window.location.search);
    } else {
      history.push(window.location.pathname + window.location.search + selectedTab);
    }
    if (selectedTab === "#trash") {
      this.loadAllTrash();
    }
  };

  render() {
    const { drafts, published, trash, location, restoreReport, intl } = this.props;
    const allReports = this.props.reports;
    const { reportsLoad, trashLoad } = this.state;
    const selected = location.hash || this.defaultTab;
    const reports = {
      "#drafts": drafts,
      "#published": published,
      "#trash": trash ? trash.reportIds.map((id) => allReports.get(id)).filter((r) => r.deletedAt) : Seq(),
    }[selected];
    return (
      <div className={styles.DraftList}>
        <Helmet title={`${fm(intl, featureNames.rideReports)} · ${fm(intl, rrMessages.yourReports)}`} />
        <h1 className={cx(appStyles.pageTitle, styles.pageTitleOverride)}>
          <Msg m={rrMessages.yourReports} />
        </h1>
        <TabNav onChange={this.onChangeTab} selected={selected} styles={TabNavSubtle}>
          <Tab id="#drafts">
            <Msg m={rrMessages.drafts} />
          </Tab>
          <Tab id="#published">
            <Msg m={rrMessages.published} />
          </Tab>
          <Tab id="#trash">
            <T id="DraftList.trash" defaultMessage="Trash" />
          </Tab>
        </TabNav>
        {selected === "#trash" && (
          <p className={styles.hint}>
            <T id="DraftList.trashHint" defaultMessage="Reports in trash are deleted after 30 days" />
          </p>
        )}
        {reports.length === 0 && reportsLoad === "completed" && noReportsMessage}
        {reports.map((d) => (
          <DraftItem key={d.id} draft={d} requestDelete={() => this.deleteReport(d)} requestRestore={restoreReport} />
        ))}
        {selected !== "#trash" && reportsLoad === "pending" && (
          <div className={styles.spinner}>
            <CircleSpinner size={25} />
          </div>
        )}
        {selected === "#trash" && trashLoad === "pending" && (
          <div className={styles.spinner}>
            <CircleSpinner size={25} />
          </div>
        )}
      </div>
    );
  }
}

const DraftItem = ({ draft, requestDelete, requestRestore }) => {
  const editUrl = `/ride_reports/${draft.slug}/edit`;
  const viewUrl = `/ride_reports/${draft.slug}`;
  return (
    <div>
      <h2 className={styles.draftTitle}>
        <Link to={draft.publishStatus === "draft" ? editUrl : viewUrl}>{draft.title}</Link>
      </h2>
      <p className={styles.draftMeta}>
        <T id="DraftList.lastEdited" defaultMessage="Last edited" /> {moment(draft.updatedAt).fromNow()}
        {" ∙ "}
        <Link to={viewUrl} className={styles.viewLink}>
          <T id="DraftList.view" defaultMessage="view" />
        </Link>
        {!draft.deletedAt && " ∙ "}
        {!draft.deletedAt && (
          <Link to={editUrl} className={styles.editLink}>
            <Msg lowerCase m={commonVerbs.edit} />
          </Link>
        )}
        {" ∙ "}
        {draft.deletedAt ? (
          <a className={styles.restoreLink} onClick={() => requestRestore(draft.id)}>
            <T id="DraftList.restore" defaultMessage="restore" />
          </a>
        ) : (
          <a className={styles.deleteLink} onClick={requestDelete}>
            <Msg lowerCase m={commonVerbs.delete} />
          </a>
        )}
      </p>
    </div>
  );
};

const compareDates = (a, b) => {
  const dateA = new Date(a.updatedAt);
  const dateB = new Date(b.updatedAt);
  return dateB.getTime() - dateA.getTime();
};

const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
  topic: state.rideReports.topics.get(`user/${state.currentUser.id}`),
  drafts: state.rideReports.reports
    .valueSeq()
    .filter((r) => r.publishStatus === "draft" && !r.deletedAt)
    .sort(compareDates)
    .toJS(),
  published: state.rideReports.reports
    .valueSeq()
    .filter((r) => r.user.id === state.currentUser.id)
    .filter((r) => r.publishStatus !== "draft" && !r.deletedAt)
    .sort(compareDates)
    .toJS(),
  reports: state.rideReports.reports,
  trash: state.rideReports.topics.get("deleted"),
});

const mapDispatchToProps = (dispatch) => ({
  loadUserReports: (userId, page) => dispatch(loadUserReports(userId, page)),
  deleteReport: (report) => dispatch(deleteReport(report)),
  loadTrash: (page) => dispatch(loadReportsForIndex("deleted", page)),
  restoreReport: (reportId) => dispatch(restoreReport(reportId)),
});

export const DraftListContainer = connect(mapStateToProps, mapDispatchToProps)(withRouter(injectIntl(DraftList)));
